import { useLocalStorage } from "@getsoren/react-utils";
import { useAmplitude, useAuth, useChat } from "@getsoren/shared-module";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { SAAS_AXIOS_INSTANCE } from "@/api/customSaasInstance";
import { ApiCommonModelsUsersUserExtend } from "@/api/saas/model";
import { getV1SetupGetQueryKey, useV1SetupGet } from "@/api/saas/services/tenant-meta/tenant-meta";
import LOCAL_STORAGE_KEY from "@/constants/localStorageKey";
import { decodeToken } from "@/utils/token";

export interface User extends Partial<ApiCommonModelsUsersUserExtend> {
  accessToken: string;
  permissions?: string[];
  tokenType?: string;
}

const useUser = () => {
  const [userLocal, setUserLocal, removeUserLocal] = useLocalStorage<null | User>(LOCAL_STORAGE_KEY.user);
  const { clearAuthenticationToken } = useAuth({ axios: SAAS_AXIOS_INSTANCE });
  const { roleName } = userLocal?.entity?.role || {};
  const { reset } = useAmplitude();
  const queryClient = useQueryClient();
  const isLogged = !!userLocal?.accessToken && !!userLocal?.email;
  const { disconnect } = useChat({ enabled: isLogged });

  const { data: setup } = useV1SetupGet({
    query: { enabled: isLogged, refetchInterval: 60 * 60 * 1000, staleTime: Number.POSITIVE_INFINITY },
  });

  const { features } = setup || {};

  const permissions = useMemo(() => {
    if (features) {
      return Object.entries(features).reduce<string[]>((acc, [feature, roles]) => {
        if (roles.includes(String(roleName))) {
          acc.push(feature);
        }
        return acc;
      }, []);
    }
    return decodeToken(userLocal?.accessToken)?.scope ?? [];
  }, [features, roleName, userLocal?.accessToken]);

  const setUser = useCallback(
    (newUser?: User) => {
      if (!newUser) {
        return;
      }

      queryClient.removeQueries({ queryKey: getV1SetupGetQueryKey() }); // Clear cached setup data to force a fresh fetch with the new user's token (avoids stale tenant features across sessions)
      const decoded = decodeToken(newUser.accessToken);

      setUserLocal((prevState) => ({
        ...prevState,
        ...newUser,
        permissions: decoded?.scope ?? [],
      }));
    },
    [queryClient, setUserLocal],
  );

  const signOut = useCallback(() => {
    clearAuthenticationToken();
    removeUserLocal();
    disconnect();
    reset();
    queryClient.clear();
  }, [clearAuthenticationToken, removeUserLocal, disconnect, reset, queryClient]);

  /**
   * Refresh user permissions
   */
  useEffect(() => {
    if (features) {
      setUserLocal((prev) => (prev ? { ...prev, permissions } : prev));
    }
  }, [permissions, features, setUserLocal]);

  const user = userLocal
    ? {
        ...userLocal,
        tenantName: userLocal.tenantName,
      }
    : null;

  return {
    ...user,
    isLogged,
    permissions,
    setUser,
    signOut,
  };
};

export default useUser;
